import { useState, useEffect, useRef } from 'react'
import confetti from 'canvas-confetti'
import { playSubtaskCheck, playTaskComplete } from '@/utils/feedback'

const PRESETS = [
  { label: '15m', seconds: 15 * 60 },
  { label: '25m', seconds: 25 * 60 },
  { label: '45m', seconds: 45 * 60 },
  { label: '60m', seconds: 60 * 60 },
]

function formatTimer(seconds) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function FocusMode({ task, startSubtaskId, onToggleSubtask, onClose }) {
  const incompleteSubtasks = task.subtasks.filter((st) => !st.done)

  // Find starting subtask index in the full array
  const initialIndex = startSubtaskId
    ? task.subtasks.findIndex((st) => st.id === startSubtaskId)
    : task.subtasks.findIndex((st) => !st.done)

  const [currentIndex, setCurrentIndex] = useState(Math.max(initialIndex, 0))
  const [duration, setDuration] = useState(25 * 60)
  const [timeRemaining, setTimeRemaining] = useState(25 * 60)
  const [isRunning, setIsRunning] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [sessionComplete, setSessionComplete] = useState(false)
  const [completedDuringSession, setCompletedDuringSession] = useState(0)
  const startTimeRef = useRef(null)
  const [timeSpent, setTimeSpent] = useState(0)

  const currentSubtask = task.subtasks[currentIndex]

  // Timer countdown
  useEffect(() => {
    if (!isRunning || isPaused) return
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setSessionComplete(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [isRunning, isPaused])

  // Fire confetti + sound on session complete
  useEffect(() => {
    if (sessionComplete) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: compute and display time spent when session ends
      setTimeSpent(startTimeRef.current
        ? Math.round((Date.now() - startTimeRef.current) / 60000)
        : 0)
      playTaskComplete()
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { x: 0.5, y: 0.4 },
        zIndex: 10001,
        disableForReducedMotion: true,
      })
    }
  }, [sessionComplete])

  function handleStart() {
    setIsRunning(true)
    startTimeRef.current = Date.now()
  }

  function handleSelectPreset(seconds) {
    setDuration(seconds)
    setTimeRemaining(seconds)
  }

  function handleMarkDone() {
    if (!currentSubtask || currentSubtask.done) return
    playSubtaskCheck()
    onToggleSubtask(task.id, currentSubtask.id)
    setCompletedDuringSession((c) => c + 1)

    // Advance to next incomplete subtask
    const nextIndex = task.subtasks.findIndex((st, i) => i > currentIndex && !st.done)
    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex)
    } else {
      // All done
      setSessionComplete(true)
    }
  }

  function handleStop() {
    setSessionComplete(true)
  }

  // SVG progress ring calculations
  const radius = 108
  const circumference = 2 * Math.PI * radius
  const progress = duration > 0 ? timeRemaining / duration : 1
  const dashOffset = circumference * (1 - progress)

  // Pre-start: timer preset selection
  if (!isRunning) {
    return (
      <div className="focus-overlay">
        <div className="focus-content">
          <p className="focus-task-name">{task.title}</p>
          <h2 className="focus-subtask-name">
            {currentSubtask ? currentSubtask.title : 'All steps done!'}
          </h2>

          <div className="focus-presets">
            <p className="focus-presets-label">Session length</p>
            <div className="focus-preset-row">
              {PRESETS.map((p) => (
                <button
                  key={p.seconds}
                  className={`focus-preset-pill${duration === p.seconds ? ' focus-preset-pill--active' : ''}`}
                  onClick={() => handleSelectPreset(p.seconds)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="focus-timer-display">
            <span className="focus-timer-big">{formatTimer(timeRemaining)}</span>
          </div>

          <div className="focus-controls">
            <button className="btn btn-primary focus-btn-start" onClick={handleStart}>
              Start Focus
            </button>
            <button className="btn btn-secondary focus-btn-close" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Session complete: summary
  if (sessionComplete) {
    return (
      <div className="focus-overlay">
        <div className="focus-content">
          <div className="focus-summary">
            <h2 className="focus-summary-title">Session Complete</h2>
            <p className="focus-task-name">{task.title}</p>
            <div className="focus-summary-stats">
              <div className="focus-stat">
                <span className="focus-stat-value">{completedDuringSession}</span>
                <span className="focus-stat-label">steps done</span>
              </div>
              <div className="focus-stat">
                <span className="focus-stat-value">{timeSpent}m</span>
                <span className="focus-stat-label">focused</span>
              </div>
              <div className="focus-stat">
                <span className="focus-stat-value">
                  {incompleteSubtasks.length - completedDuringSession}
                </span>
                <span className="focus-stat-label">remaining</span>
              </div>
            </div>
          </div>
          <button className="btn btn-primary focus-btn-done" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    )
  }

  // Active focus: timer + current subtask
  return (
    <div className="focus-overlay">
      <div className="focus-content">
        <p className="focus-task-name">{task.title}</p>

        <div className="focus-ring-container">
          <svg className="focus-timer-ring" viewBox="0 0 240 240">
            <circle
              className="focus-ring-track"
              cx="120"
              cy="120"
              r={radius}
              fill="none"
              strokeWidth="8"
            />
            <circle
              className="focus-ring-progress"
              cx="120"
              cy="120"
              r={radius}
              fill="none"
              strokeWidth="8"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
              transform="rotate(-90 120 120)"
            />
          </svg>
          <div className="focus-ring-inner">
            <span className="focus-timer-text">{formatTimer(timeRemaining)}</span>
          </div>
        </div>

        <h2 className="focus-subtask-name">
          {currentSubtask ? currentSubtask.title : 'All steps done!'}
        </h2>
        {currentSubtask && (
          <p className="focus-subtask-time">
            ~{currentSubtask.timeEstimate} min estimated
          </p>
        )}

        <div className="focus-controls">
          <button
            className="btn btn-secondary focus-btn-pause"
            onClick={() => setIsPaused((p) => !p)}
          >
            {isPaused ? 'Resume' : 'Pause'}
          </button>
          {currentSubtask && !currentSubtask.done && (
            <button className="btn btn-primary focus-btn-done" onClick={handleMarkDone}>
              Done
            </button>
          )}
          <button className="btn btn-secondary focus-btn-stop" onClick={handleStop}>
            Stop
          </button>
        </div>
      </div>
    </div>
  )
}

export default FocusMode
